import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import confetti from 'canvas-confetti';

interface TldStat {
  tld: string;
  count: number;
}

interface SubmitResponse {
  success: boolean;
  message?: string;
  error?: string;
  remaining_credits?: number;
  submitted_count?: number;
}

interface LinksResponse {
  success: boolean;
  data: {
    pending?: number;
    submitted?: number;
    rejected?: number;
  };
}

@Component({
  selector: 'app-submit-links',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './submit-links.html',
  styleUrls: ['./submit-links.scss']
})
export class SubmitLinksComponent implements OnInit {
  submitMethod: 'text' | 'file' = 'text';
  urlsText = '';
  selectedFile: File | null = null;
  fileUrlsText = '';
  fileUrlCount = 0;
  isSubmitting = false;
  successMessage = '';
  errorMessage = '';
  isSuccess = false;
  
  userCredits = 0;
  totalSubmitted = 0;
  pendingCount = 0;
  
  // Drip settings
  dripEnabled = false;
  dripInterval = 1;
  priority: 'normal' | 'high' | 'stealth' = 'normal';

  // Wizard state
  currentStep = 1;

  // Real-time scanner stats
  uniqueUrls: string[] = [];
  duplicateCount = 0;
  tldStats: TldStat[] = [];
  estimatedCredits = 0;

  constructor(private http: HttpClient) {}

  ngOnInit() {
    this.loadUserData();
  }

  setSubmitMethod(method: 'text' | 'file') {
    this.submitMethod = method;
    this.clearMessages();
    this.estimatedCredits = method === 'text' ? this.uniqueUrls.length : this.fileUrlCount;
  }

  getUrlCount(): number {
    return this.submitMethod === 'text' ? this.uniqueUrls.length : this.fileUrlCount;
  }

  onUrlsChange() {
    if (!this.urlsText.trim()) {
      this.uniqueUrls = [];
      this.duplicateCount = 0;
      this.tldStats = [];
      this.estimatedCredits = 0;
      return;
    }

    const lines = this.urlsText.split('\n').map(l => l.trim()).filter(l => l.length > 0);
    const unique = Array.from(new Set(lines));
    
    // Validate URLs (must start with http:// or https:// and contain a dot)
    const validUrls = unique.filter(url => {
      return (url.startsWith('http://') || url.startsWith('https://')) && url.includes('.');
    });

    this.uniqueUrls = validUrls;
    this.duplicateCount = lines.length - validUrls.length;
    this.estimatedCredits = validUrls.length;

    // TLD grouping
    const tldMap = new Map<string, number>();
    validUrls.forEach(url => {
      try {
        const hostname = new URL(url).hostname;
        const parts = hostname.split('.');
        if (parts.length > 1) {
          const tld = parts[parts.length - 1].toLowerCase();
          tldMap.set(tld, (tldMap.get(tld) || 0) + 1);
        }
      } catch (e) {
        // Fallback simple parsing if URL constructor fails
        const match = url.match(/https?:\/\/([^/:]+)/i);
        if (match && match[1]) {
          const parts = match[1].split('.');
          if (parts.length > 1) {
            const tld = parts[parts.length - 1].toLowerCase();
            tldMap.set(tld, (tldMap.get(tld) || 0) + 1);
          }
        }
      }
    });

    this.tldStats = Array.from(tldMap.entries())
      .map(([tld, count]) => ({ tld, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5); // Keep top 5 TLDs
  }

  submitLinks() {
    const count = this.getUrlCount();
    if (count === 0) return;

    this.isSubmitting = true;
    this.clearMessages();

    const token = localStorage.getItem('auth_token');

    if (this.submitMethod === 'text') {
      this.http.post<SubmitResponse>(`${environment.apiUrl}/links/submit-text.php`,
        {
          urls: this.uniqueUrls.join('\n'),
          drip_enabled: this.dripEnabled,
          drip_interval: this.dripInterval
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      ).subscribe({
        next: (response) => {
          if (response.success) {
            this.successMessage = response.message || `Successfully submitted ${count} URLs for indexing!`;
            this.isSuccess = true;
            this.updateUserCredits(response.remaining_credits ?? 0, response.submitted_count ?? 0);
            this.loadUserData();
            this.triggerConfetti();
          } else {
            this.errorMessage = response.error || 'Submission failed';
          }
          this.isSubmitting = false;
        },
        error: (error: HttpErrorResponse) => {
          this.errorMessage = error.error?.error || 'Submission failed. Please try again.';
          this.isSubmitting = false;
        }
      });
    } else {
      if (!this.selectedFile || this.fileUrlCount === 0) {
        this.isSubmitting = false;
        return;
      }

      this.http.post<SubmitResponse>(`${environment.apiUrl}/links/submit-text.php`,
        {
          urls: this.fileUrlsText,
          drip_enabled: this.dripEnabled,
          drip_interval: this.dripInterval
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      ).subscribe({
        next: (response) => {
          if (response.success) {
            this.successMessage = response.message || `Successfully submitted ${this.fileUrlCount} URLs from file!`;
            this.isSuccess = true;
            this.updateUserCredits(response.remaining_credits ?? 0, response.submitted_count ?? 0);
            this.loadUserData();
            this.triggerConfetti();
          } else {
            this.errorMessage = response.error || 'Submission failed';
          }
          this.isSubmitting = false;
        },
        error: (error: HttpErrorResponse) => {
          this.errorMessage = error.error?.error || 'Submission failed. Please try again.';
          this.isSubmitting = false;
        }
      });
    }
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.selectedFile = file;
      this.readFileContent(file);
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.selectedFile = files[0];
      this.readFileContent(files[0]);
    }
  }

  readFileContent(file: File) {
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      const lines = content.split('\n').map(l => l.trim()).filter(l => l.length > 0);
      
      // Validate URLs (must start with http:// or https:// and contain a dot)
      const validUrls = lines.filter(url => {
        return (url.startsWith('http://') || url.startsWith('https://')) && url.includes('.');
      });

      this.fileUrlsText = validUrls.join('\n');
      this.fileUrlCount = validUrls.length;
      this.estimatedCredits = validUrls.length;
    };
    reader.readAsText(file);
  }

  removeFile() {
    this.selectedFile = null;
    this.fileUrlCount = 0;
    this.estimatedCredits = 0;
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1);
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  private loadUserData() {
    const user = JSON.parse(localStorage.getItem('userData') || '{}');
    this.userCredits = user.credits || 0;
    this.totalSubmitted = user.totalLinks || 0;

    const token = localStorage.getItem('auth_token');
    if (token) {
      this.http.get<LinksResponse>(`${environment.apiUrl}/user/links-simple.php`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).subscribe({
        next: (response) => {
          if (response.success) {
            this.pendingCount = response.data.pending || 0;
          }
        },
        error: (error: HttpErrorResponse) => { /* Silent error handling */ }
      });
    }
  }

  private clearMessages() {
    this.successMessage = '';
    this.errorMessage = '';
    this.isSuccess = false;
  }

  private updateUserCredits(remainingCredits: number, submittedCount: number) {
    // Update local user data
    const user = JSON.parse(localStorage.getItem('userData') || '{}');
    user.credits = remainingCredits;
    user.totalLinks = (user.totalLinks || 0) + submittedCount;
    localStorage.setItem('userData', JSON.stringify(user));
    
    // Update component state
    this.userCredits = remainingCredits;
    this.totalSubmitted = user.totalLinks;
    
    // Trigger navbar update
    window.dispatchEvent(new Event('storage'));
  }

  nextStep() {
    if (this.currentStep === 1) {
      if (this.submitMethod === 'text' && this.getUrlCount() === 0) return;
      if (this.submitMethod === 'file' && (!this.selectedFile || this.fileUrlCount === 0)) return;
    }
    if (this.currentStep < 2) {
      this.currentStep++;
    }
  }

  prevStep() {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  goToStep(step: number) {
    if (step >= 1 && step <= 2) {
      this.currentStep = step;
    }
  }

  triggerConfetti() {
    const duration = 3 * 1000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

    const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

    const interval: any = setInterval(function() {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);
      confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } });
      confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } });
    }, 250);
  }

  resetWizard() {
    this.currentStep = 1;
    this.urlsText = '';
    this.uniqueUrls = [];
    this.duplicateCount = 0;
    this.tldStats = [];
    this.estimatedCredits = 0;
    this.removeFile();
    this.clearMessages();
  }
}